import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';

const TechnicalSpecifications = () => {
  const [activeTab, setActiveTab] = useState('fiber-types');
  const [expandedSpec, setExpandedSpec] = useState(null);

  const tabs = [
    { id: 'fiber-types', label: 'Fiber Types', icon: 'Cable' },
    { id: 'installation', label: 'Installation Methods', icon: 'Settings' },
    { id: 'compliance', label: 'Compliance Standards', icon: 'Shield' },
    { id: 'equipment', label: 'Equipment Specs', icon: 'HardDrive' }
  ];

  const specifications = {
    'fiber-types': [
      {
        title: "Single-Mode Fiber (SMF)",
        description: "Long-distance, high-bandwidth applications",
        specs: {
          "Core Diameter": "9 μm",
          "Cladding Diameter": "125 μm",
          "Wavelength": "1310nm, 1550nm",
          "Distance": "Up to 40km+",
          "Bandwidth": "Unlimited",
          "Applications": "Campus backbone, long-distance connections"
        },
        advantages: [
          "Highest bandwidth capacity",
          "Longest transmission distances",
          "Future-proof technology",
          "Lower signal loss"
        ]
      },
      {
        title: "Multi-Mode Fiber (MMF)",
        description: "Short to medium distance applications",
        specs: {
          "Core Diameter": "50 μm or 62.5 μm",
          "Cladding Diameter": "125 μm",
          "Wavelength": "850nm, 1300nm",
          "Distance": "Up to 2km",
          "Bandwidth": "High capacity",
          "Applications": "Building backbone, floor distribution"
        },
        advantages: [
          "Cost-effective for shorter distances",
          "Compatible with LED sources",
          "Easier to terminate",
          "Good for building networks"
        ]
      },
      {
        title: "Armored Fiber Cable",
        description: "Enhanced protection for harsh environments",
        specs: {
          "Protection": "Steel armor or kevlar",
          "Environment": "Outdoor, underground, industrial",
          "Temperature": "-40°C to +70°C",
          "Crush Resistance": "Up to 1000 N/cm",
          "Moisture": "Water-blocked design",
          "Applications": "Outdoor runs, underground conduits"
        },
        advantages: [
          "Superior physical protection",
          "Rodent resistant",
          "Weather resistant",
          "Long-term durability"
        ]
      }
    ],
    'installation': [
      {
        title: "Direct Burial Installation",
        description: "Underground fiber installation method",
        specs: {
          "Depth": "36-48 inches minimum",
          "Conduit": "HDPE or PVC recommended",
          "Marking": "Tracer wire and warning tape",
          "Protection": "Armored cable required",
          "Bend Radius": "20x cable diameter",
          "Applications": "Campus connections, building-to-building"
        },
        advantages: [
          "Protected from weather",
          "Secure installation",
          "Long-term reliability",
          "Minimal visual impact"
        ]
      },
      {
        title: "Aerial Installation",
        description: "Overhead fiber installation on poles",
        specs: {
          "Support": "Self-supporting or messenger wire",
          "Span Length": "Up to 200 feet typical",
          "Wind Load": "Designed for local conditions",
          "Ice Load": "Regional requirements",
          "Clearance": "Per local electrical codes",
          "Applications": "Utility pole runs, building connections"
        },
        advantages: [
          "Faster installation",
          "Lower installation cost",
          "Easy access for maintenance",
          "Flexible routing options"
        ]
      },
      {
        title: "Indoor Plenum Installation",
        description: "Fire-rated cable for air handling spaces",
        specs: {
          "Rating": "OFNP (Plenum rated)",
          "Fire Test": "UL 910 Steiner Tunnel",
          "Smoke": "Low smoke generation",
          "Temperature": "Operating: -20°C to +70°C",
          "Bend Radius": "10x cable diameter",
          "Applications": "Ceiling spaces, HVAC areas"
        },
        advantages: [
          "Code compliant installation",
          "Fire safety rated",
          "Low smoke emission",
          "Suitable for air spaces"
        ]
      }
    ],
    'compliance': [
      {
        title: "TIA/EIA Standards",
        description: "Telecommunications Industry Association standards",
        specs: {
          "TIA-568": "Commercial building cabling",
          "TIA-569": "Pathways and spaces",
          "TIA-606": "Administration standard",
          "TIA-607": "Grounding and bonding",
          "TIA-758": "Customer-owned outside plant",
          "Applications": "All commercial installations"
        },
        advantages: [
          "Industry standard compliance",
          "Interoperability assurance",
          "Future upgrade compatibility",
          "Professional installation"
        ]
      },
      {
        title: "Building Codes",
        description: "Local and national building code compliance",
        specs: {
          "NEC Article 770": "Optical fiber cables",
          "IBC Chapter 27": "Electrical systems",
          "Local Codes": "Municipal requirements",
          "Fire Rating": "Plenum, riser, general purpose",
          "Pathway": "Conduit and cable tray rules",
          "Applications": "All building installations"
        },
        advantages: [
          "Legal compliance",
          "Safety assurance",
          "Inspection approval",
          "Insurance requirements"
        ]
      },
      {
        title: "Safety Standards",
        description: "Worker and installation safety requirements",
        specs: {
          "OSHA 1926": "Construction safety",
          "NECA/BICSI": "Installation practices",
          "Laser Safety": "IEC 60825 compliance",
          "PPE Requirements": "Safety equipment standards",
          "Training": "Certified technician requirements",
          "Applications": "All installation work"
        },
        advantages: [
          "Worker safety protection",
          "Liability reduction",
          "Professional standards",
          "Quality assurance"
        ]
      }
    ],
    'equipment': [
      {
        title: "Fiber Optic Transceivers",
        description: "Network interface equipment specifications",
        specs: {
          "SFP/SFP+": "1G/10G Ethernet",
          "QSFP+/QSFP28": "40G/100G Ethernet",
          "Wavelength": "850nm, 1310nm, 1550nm",
          "Distance": "Up to 40km+",
          "Power": "Low power consumption",
          "Applications": "Switch and router interfaces"
        },
        advantages: [
          "High-speed connectivity",
          "Hot-swappable modules",
          "Multiple speed options",
          "Vendor interoperability"
        ]
      },
      {
        title: "Fiber Distribution Panels",
        description: "Centralized fiber management systems",
        specs: {
          "Capacity": "12-144 fiber terminations",
          "Connectors": "LC, SC, ST, MTP/MPO",
          "Mounting": "19-inch rack or wall mount",
          "Management": "Cable routing and storage",
          "Access": "Front and rear access",
          "Applications": "Data centers, telecom rooms"
        },
        advantages: [
          "Organized cable management",
          "Easy maintenance access",
          "Scalable design",
          "Professional appearance"
        ]
      },
      {
        title: "Optical Test Equipment",
        description: "Installation and maintenance test tools",
        specs: {
          "OTDR": "Optical time domain reflectometer",
          "Power Meter": "Optical power measurement",
          "Light Source": "Stable test signal generation",
          "Inspection": "Fiber end-face microscope",
          "Certification": "Tier 1/Tier 2 testing",
          "Applications": "Installation verification, troubleshooting"
        },
        advantages: [
          "Installation verification",
          "Performance validation",
          "Troubleshooting capability",
          "Documentation compliance"
        ]
      }
    ]
  };

  const toggleSpecExpansion = (index) => {
    setExpandedSpec(expandedSpec === index ? null : index);
  };

  return (
    <div className="bg-white rounded-xl shadow-brand-strong p-8 mb-12">
      <div className="text-center mb-8">
        <h2 className="text-3xl font-bold text-text-primary mb-4">
          Technical Specifications
        </h2>
        <p className="text-text-secondary text-lg">
          Detailed technical information for IT professionals and decision makers
        </p>
      </div>
      {/* Tab Navigation */}
      <div className="flex flex-wrap justify-center gap-2 mb-8">
        {tabs?.map((tab) => (
          <button
            key={tab?.id}
            onClick={() => setActiveTab(tab?.id)}
            className={`flex items-center space-x-2 px-4 py-3 rounded-lg font-medium transition-all duration-200 ${
              activeTab === tab?.id
                ? 'bg-primary text-white shadow-brand-subtle'
                : 'bg-gray-100 text-text-primary hover:bg-gray-200'
            }`}
          >
            <Icon name={tab?.icon} size={18} />
            <span className="hidden sm:inline">{tab?.label}</span>
          </button>
        ))}
      </div>
      {/* Specifications Content */}
      <div className="space-y-6">
        {specifications?.[activeTab]?.map((spec, index) => (
          <div key={index} className="border border-gray-200 rounded-lg overflow-hidden">
            <button
              onClick={() => toggleSpecExpansion(index)}
              className="w-full px-6 py-4 text-left flex items-center justify-between hover:bg-gray-50 transition-colors duration-200"
            >
              <div>
                <h3 className="text-lg font-semibold text-text-primary mb-1">
                  {spec?.title}
                </h3>
                <p className="text-text-secondary text-sm">
                  {spec?.description}
                </p>
              </div>
              <Icon 
                name={expandedSpec === index ? "ChevronUp" : "ChevronDown"} 
                size={20} 
                className="text-text-secondary transition-transform duration-200" 
              />
            </button>
            
            <div className={`expandable-content ${expandedSpec === index ? 'expanded' : ''}`}>
              <div className="px-6 pb-6 border-t bg-gray-50">
                <div className="grid md:grid-cols-2 gap-6 mt-6">
                  {/* Specifications Table */}
                  <div>
                    <h4 className="font-semibold text-text-primary mb-4">Specifications</h4>
                    <div className="space-y-2">
                      {Object.entries(spec?.specs)?.map(([key, value]) => (
                        <div key={key} className="flex justify-between items-center py-2 border-b border-gray-200 last:border-b-0">
                          <span className="font-medium text-gray-700">{key}:</span>
                          <span className="text-text-secondary text-sm">{value}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Advantages */}
                  <div>
                    <h4 className="font-semibold text-text-primary mb-4">Key Advantages</h4>
                    <ul className="space-y-2">
                      {spec?.advantages?.map((advantage, advIndex) => (
                        <li key={advIndex} className="flex items-start">
                          <Icon name="Check" size={16} className="mr-3 mt-1 text-accent flex-shrink-0" />
                          <span className="text-text-secondary text-sm">{advantage}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
      <div className="mt-8 text-center">
        <div className="bg-primary/10 border border-primary/20 rounded-lg p-6">
          <div className="flex items-center justify-center mb-4">
            <Icon name="Award" size={24} className="mr-3 text-primary" />
            <h3 className="text-xl font-semibold text-text-primary">
              Certified Installation Team
            </h3>
          </div>
          <p className="text-text-secondary mb-6">
            Our technicians are certified in all major fiber optic standards and 
            use only industry-approved equipment and installation methods.
          </p>
          <div className="flex flex-wrap justify-center gap-4 text-sm">
            <div className="flex items-center text-primary">
              <Icon name="CheckCircle" size={16} className="mr-2" />
              BICSI Certified
            </div>
            <div className="flex items-center text-primary">
              <Icon name="CheckCircle" size={16} className="mr-2" />
              FOA Approved
            </div>
            <div className="flex items-center text-primary">
              <Icon name="CheckCircle" size={16} className="mr-2" />
              NECA Standards
            </div>
            <div className="flex items-center text-primary">
              <Icon name="CheckCircle" size={16} className="mr-2" />
              TIA Compliant
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TechnicalSpecifications;